// レシートの「場所」セクション
//
// 場所はレシートの中身（review_status）とは別軸で確定していく。
// 中身が確定済みでも場所は推定のまま、ということがあるため分けている。
import { format } from 'date-fns'
import { CalendarDays, Check, Clock, MapPin, RotateCcw, Search } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'

import type { ReceiptDetail, ReceiptPlaceMatchSource } from '@/api/types/receipt'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { parseUtcDate } from '@/lib/date-utils'
import { PATHS } from '@/routes/paths'

import {
  useConfirmReceiptPlace,
  useConfirmReceiptVisit,
  useReceiptVisitCandidates,
  useUnconfirmReceiptPlace,
} from '../hooks/use-receipts'
import { ReceiptPlaceSearchSheet } from './receipt-place-search-sheet'

const MATCH_SOURCE_LABELS: Record<ReceiptPlaceMatchSource, string> = {
  gps: '滞在履歴から',
  name: '店名から推定',
  google: 'Google検索から',
  manual: '手動で指定',
}

const formatStayRange = (arrival: string | null, departure: string | null): string | null => {
  if (!arrival) return null
  const start = format(parseUtcDate(arrival), 'HH:mm')
  const end = departure ? format(parseUtcDate(departure), 'HH:mm') : '滞在中'
  return `${start} 〜 ${end}`
}

type Props = {
  receipt: ReceiptDetail
}

export const ReceiptPlaceSection = ({ receipt }: Props) => {
  const confirmPlace = useConfirmReceiptPlace(receipt.id)
  const unconfirmPlace = useUnconfirmReceiptPlace(receipt.id)
  const confirmVisit = useConfirmReceiptVisit(receipt.id)
  const [searchOpen, setSearchOpen] = useState(false)

  const isConfirmed = receipt.place_confirmed_at !== null
  const stayRange = receipt.visit ? formatStayRange(receipt.visit.arrival_date, receipt.visit.departure_date) : null
  // 購入日のタイムラインを見れば、どこに居たのかを前後ごと確かめられる
  const purchasedDate = receipt.purchased_at ? format(parseUtcDate(receipt.purchased_at), 'yyyy-MM-dd') : null

  // 確定済みなら選び直す必要がないので取りにいかない
  const { data: visitCandidates } = useReceiptVisitCandidates(receipt.id, { enabled: !isConfirmed })
  const selectableVisits = (visitCandidates ?? []).filter(
    (visit) => visit.place_id !== null && visit.id !== receipt.visit_id,
  )

  const handleConfirm = () => {
    confirmPlace.mutate(undefined, {
      onSuccess: () => toast.success('場所を確定しました。'),
      onError: () => toast.error('場所の確定に失敗しました。'),
    })
  }

  const handleUnconfirm = () => {
    unconfirmPlace.mutate(undefined, {
      onSuccess: () => toast.success('場所の確定を取り消しました。'),
      onError: () => toast.error('取り消しに失敗しました。'),
    })
  }

  const handleSelectVisit = (visitId: number) => {
    confirmVisit.mutate(visitId, {
      onSuccess: () => toast.success('この滞在に紐付けました。'),
      onError: () => toast.error('紐付けに失敗しました。'),
    })
  }

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <h3 className="text-sm text-muted-foreground">場所</h3>
        {purchasedDate && (
          <Button asChild variant="ghost" size="sm" className="ml-auto">
            <Link to={`${PATHS.ACTIVITY}?date=${purchasedDate}`}>
              <CalendarDays className="size-4" />
              この日の行動
            </Link>
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-2 rounded border border-gray-700 p-3">
        <div className="flex items-center gap-2">
          <MapPin className="size-4 shrink-0 text-muted-foreground" />
          {receipt.place_name ? (
            <span className="truncate">{receipt.place_name}</span>
          ) : (
            <span className="text-muted-foreground">場所なし</span>
          )}
          {isConfirmed ? (
            <Badge variant="secondary" className="ml-auto shrink-0">
              確定済み
            </Badge>
          ) : (
            <Badge variant="outline" className="ml-auto shrink-0">
              未確定
            </Badge>
          )}
        </div>

        <dl className="flex flex-col gap-1 text-xs text-muted-foreground">
          <div className="flex gap-2">
            <dt className="shrink-0">根拠</dt>
            <dd>{receipt.place_match_source ? MATCH_SOURCE_LABELS[receipt.place_match_source] : '—'}</dd>
          </div>
          {stayRange && (
            <div className="flex gap-2">
              <dt className="shrink-0">滞在</dt>
              <dd>{stayRange}</dd>
            </div>
          )}
        </dl>

        {isConfirmed ? (
          <Button variant="ghost" size="sm" className="self-start" onClick={handleUnconfirm}>
            <RotateCcw className="size-4" />
            確定を取り消す
          </Button>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={receipt.place_id === null || confirmPlace.isPending}
              onClick={handleConfirm}
            >
              <Check className="size-4" />
              この場所で確定
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSearchOpen(true)}>
              <Search className="size-4" />
              Google で探す
            </Button>
          </div>
        )}
      </div>

      {!isConfirmed && selectableVisits.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {/* 道の駅のパン屋のように店名と場所名が一致しないものは自動では紐付かない */}
          <p className="text-xs text-muted-foreground">購入時刻に居た場所</p>
          {selectableVisits.map((visit) => (
            <button
              key={visit.id}
              type="button"
              disabled={confirmVisit.isPending}
              onClick={() => handleSelectVisit(visit.id)}
              className="flex items-center gap-2 rounded border border-gray-700 px-3 py-2 text-left text-sm hover:bg-gray-800 disabled:opacity-50"
            >
              <Clock className="size-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{visit.place_name ?? '場所不明'}</span>
              <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                {formatStayRange(visit.arrival_date, visit.departure_date)}
              </span>
            </button>
          ))}
        </div>
      )}

      <ReceiptPlaceSearchSheet receipt={receipt} open={searchOpen} onOpenChange={setSearchOpen} />
    </section>
  )
}
